import { ErrorMessage, Form, Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, Dropdown } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  ErrorUnexpected,
  OperationSuccessfully,
} from "../../../const/Messages";

import * as Yup from "yup";
import * as ElementService from "../../../service/ElementService";
import * as ElementTypeService from "../../../service/ElementTypeService";
import * as NeLogsFileService from "../../../service/NeLogsFileService";
import * as VendorService from "../../../service/VendorService";

const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
  <div
    className="form-control d-flex justify-content-between align-items-center"
    style={{ cursor: "pointer", backgroundColor: "#fff" }}
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
  >
    <span className="text-truncate">{children}</span>
    <span className="text-muted small">&#x25bc;</span>
  </div>
));

const CustomMenu = React.forwardRef(
  ({ children, style, className, "aria-labelledby": labeledBy }, ref) => {
    const [value, setValue] = useState("");

    return (
      <div
        ref={ref}
        style={{ ...style, zIndex: 1055, minWidth: "100%" }}
        className={className}
        aria-labelledby={labeledBy}
      >
        <div className="p-2">
          <input
            autoFocus
            className="form-control"
            placeholder="Search..."
            onChange={(e) => setValue(e.target.value)}
            value={value}
          />
        </div>
        <ul
          className="list-unstyled mb-0"
          style={{ maxHeight: "200px", overflowY: "auto" }}
        >
          {React.Children.toArray(children).filter(
            (child) =>
              !value ||
              (typeof child.props.children === "string" &&
                child.props.children
                  .toLowerCase()
                  .includes(value.toLowerCase()))
          )}
        </ul>
      </div>
    );
  }
);

const initialValues = {
  vendor: "",
  elementType: "",
  element: "",
  fileSample: null,
};

function LogSingleUploadModal({ show, setShow }) {
  const [vendorList, setVendorList] = useState([]);
  const [elementTypeList, setElementTypeList] = useState([]);
  const [elementList, setElementList] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const formikRef = useRef(null);

  const SUPPORTED_FORMATS_LOG = ["text/plain"];

  useEffect(() => {
    if (show) {
      VendorService.findAll()
        .then((vendors) => setVendorList(vendors))
        .catch(() => toast.error(ErrorUnexpected));
    }
  }, [show]);

  const schema = Yup.object().shape({
    elementType: Yup.number().required("Element Type is a required field"),
    vendor: Yup.number().required("Vendor is a required field"),
    element: Yup.number().required("Element is a required field"),
    fileSample: Yup.mixed()
      .required("File is required")
      .test("fileFormat", "Unsupported Format", (value) => {
        return (
          value &&
          (SUPPORTED_FORMATS_LOG.includes(value.type) ||
            value.name.endsWith(".log") ||
            value.name.endsWith(".txt"))
        );
      }),
  });

  function handleCloseModal() {
    setShow(false);
  }

  function loadElementTypeCombo(vendorId) {
    setElementTypeList([]);
    setElementList([]);

    formikRef.current.setFieldValue("elementType", "");
    formikRef.current.setFieldValue("element", "");

    if (vendorId) {
      ElementTypeService.findByVendorId(vendorId)
        .then((elementTypes) => setElementTypeList(elementTypes))
        .catch(() => toast.error(ErrorUnexpected));
    }
  }

  function loadElementCombo(vendorId, elementTypeId) {
    setElementList([]);
    formikRef.current.setFieldValue("element", "");

    if (vendorId && elementTypeId) {
      ElementService.getByVendorAndElementType(vendorId, elementTypeId)
        .then((elements) => {
          const activeAndSortedElements = elements
            .filter((el) => el.active === true)
            .sort((a, b) => a.name.localeCompare(b.name));

          setElementList(activeAndSortedElements);
        })
        .catch(() => toast.error(ErrorUnexpected));
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const processFile = (file, values, setFieldValue) => {
    if (!file) return;

    if (!values.element) {
      toast.warning("Please select an Element first.");
      return;
    }

    const selectedElement = elementList.find((e) => e.id === values.element);
    if (!selectedElement) return;

    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (fileExtension !== "log" && fileExtension !== "txt") {
      toast.warning(
        `File ${file.name} has invalid format. Only .log and .txt are allowed.`
      );
      return;
    }

    setFieldValue("fileSample", file);
  };

  const handleDrop = (e, values, setFieldValue) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      if (e.dataTransfer.files.length > 1) {
        toast.warning("Please drop only 1 file.");
        return;
      }
      processFile(e.dataTransfer.files[0], values, setFieldValue);
    }
  };

  const handleFileSelect = (e, values, setFieldValue) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0], values, setFieldValue);
    }
    e.target.value = null;
  };

  const removeFile = (setFieldValue) => {
    setFieldValue("fileSample", null);
  };

  function handleUpload(values, actions) {
    const form = new FormData();
    form.append("vendor", values.vendor);
    form.append("elementType", values.elementType);
    form.append("element", values.element);
    form.append("fileSample", values.fileSample);

    NeLogsFileService.upload(form)
      .then(() => {
        toast.success(OperationSuccessfully);
        handleCloseModal();
      })
      .catch(() => toast.error(ErrorUnexpected))
      .finally(() => actions.setSubmitting(false));
  }

  return (
    <Modal show={show} onHide={handleCloseModal} size="lg" animation={false}>
      <Modal.Header closeButton>
        <Modal.Title>Single Log Upload</Modal.Title>
      </Modal.Header>
      <Formik
        initialValues={initialValues}
        innerRef={formikRef}
        validationSchema={schema}
        onSubmit={handleUpload}
      >
        {({ values, setFieldValue, isSubmitting, errors }) => (
          <Form>
            <Modal.Body>
              {/* Vendor */}
              <div className="form-group row">
                <label className="col-sm-3 col-form-label">Vendor:</label>
                <div className="col-sm-9">
                  <Dropdown
                    onSelect={(eventKey) => {
                      const val = parseInt(eventKey, 10);
                      setFieldValue("vendor", val);
                      loadElementTypeCombo(val);
                    }}
                  >
                    <Dropdown.Toggle as={CustomToggle} id="dropdown-vendor">
                      {vendorList.find((v) => v.id === values.vendor)?.name ||
                        "-- Select Option --"}
                    </Dropdown.Toggle>
                    <Dropdown.Menu as={CustomMenu}>
                      {vendorList.map((vendor) => (
                        <Dropdown.Item key={vendor.id} eventKey={vendor.id}>
                          {vendor.name}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                  <span className="text-danger">
                    <ErrorMessage name="vendor" />
                  </span>
                </div>
              </div>

              {/* Element Type */}
              <div className="form-group row">
                <label className="col-sm-3 col-form-label">Element Type:</label>
                <div className="col-sm-9">
                  <Dropdown
                    onSelect={(eventKey) => {
                      const val = parseInt(eventKey, 10);
                      setFieldValue("elementType", val);
                      loadElementCombo(values.vendor, val);
                    }}
                  >
                    <Dropdown.Toggle
                      as={CustomToggle}
                      id="dropdown-elementType"
                    >
                      {elementTypeList.find((e) => e.id === values.elementType)
                        ?.name || "-- Select Option --"}
                    </Dropdown.Toggle>
                    <Dropdown.Menu as={CustomMenu}>
                      {elementTypeList.map((elementType) => (
                        <Dropdown.Item
                          key={elementType.id}
                          eventKey={elementType.id}
                        >
                          {elementType.name}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                  <span className="text-danger">
                    <ErrorMessage name="elementType" />
                  </span>
                </div>
              </div>

              {/* Element */}
              <div className="form-group row">
                <label className="col-sm-3 col-form-label">Element:</label>
                <div className="col-sm-9">
                  <Dropdown
                    onSelect={(eventKey) => {
                      setFieldValue("element", parseInt(eventKey, 10));
                      setFieldValue("fileSample", null);
                    }}
                  >
                    <Dropdown.Toggle as={CustomToggle} id="dropdown-element">
                      {elementList.find((e) => e.id === values.element)?.name ||
                        "-- Select Option --"}
                    </Dropdown.Toggle>
                    <Dropdown.Menu as={CustomMenu}>
                      {elementList.map((element) => (
                        <Dropdown.Item key={element.id} eventKey={element.id}>
                          {element.name}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                  <span className="text-danger">
                    <ErrorMessage name="element" />
                  </span>
                </div>
              </div>

              {/* File Sample (sempre obrigatório) */}
              <div className="form-group row">
                <label htmlFor="fileSample" className="col-sm-3 col-form-label">
                  {values.elementType === 15
                    ? "Audit Operadoras Bloqueadas:"
                    : "File:"}
                </label>
                <div className="col-sm-9">
                  {!values.fileSample ? (
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, values, setFieldValue)}
                      style={{
                        border: `2px dashed ${
                          isDragging ? "#007bff" : "#ced4da"
                        }`,
                        backgroundColor: isDragging ? "#e9ecef" : "#f8f9fa",
                        padding: "20px",
                        textAlign: "center",
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s ease-in-out",
                      }}
                      onClick={() =>
                        document.getElementById("singleFileInput").click()
                      }
                    >
                      <h6 className="text-muted mb-1">
                        {isDragging
                          ? "Drop file here!"
                          : "Drag your file here or click to select"}
                      </h6>
                      <small className="text-muted">
                        Supported formats: .log, .txt
                      </small>

                      <input
                        type="file"
                        id="singleFileInput"
                        style={{ display: "none" }}
                        onChange={(e) =>
                          handleFileSelect(e, values, setFieldValue)
                        }
                        accept=".log,.txt"
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        border: "1px solid #ced4da",
                        borderRadius: "8px",
                        padding: "10px",
                        backgroundColor: "#fff",
                      }}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <span className="text-truncate mr-2">
                        {values.fileSample.name}
                      </span>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => removeFile(setFieldValue)}
                        title="Remove file"
                      >
                        X
                      </Button>
                    </div>
                  )}
                  <span className="text-danger">
                    <ErrorMessage name="fileSample" />
                  </span>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className="mt-3 mr-4 d-flex justify-content-end">
                <Button
                  className="mr-1"
                  onClick={handleCloseModal}
                  variant="primary"
                  type="button"
                >
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={isSubmitting}>
                  Upload
                </Button>
              </div>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}

export default LogSingleUploadModal;
